import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCart, clearCart, updateItemQuantity, removeItem, setCartLoading } from "../../redux/hejalSlice";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import toast from "react-hot-toast";
import {
  Trash2,
  ShoppingBag,
  ArrowLeft,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Tag,
  ShoppingCart,
  AlertCircle,
} from "lucide-react";

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // REDUX STATE
  const cartItems = useSelector((state) => state.hejal.cartItems) || [];
  const isLoggedIn = useSelector((state) => state.hejal.isLoggedIn);
  const isProfileLoading = useSelector((state) => state.hejal.profileLoading);
  const cartLoading = useSelector((state) => state.hejal.cartLoading);
  const userProfile = useSelector((state) => state.hejal.profile);
  
  // LOCAL STATE
  const [processingItems, setProcessingItems] = useState(new Set());
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [cartSummaryData, setCartSummaryData] = useState({
    totalItems: 0,
    totalQuantity: 0,
    totalAmount: 0,
    items: []
  });

  console.log("Cart Items from Redux:", cartItems);
  console.log("Cart Summary Data:", cartSummaryData);

  // FETCH CART ON MOUNT
  useEffect(() => {
    if (isLoggedIn && !isProfileLoading) {
      fetchCartItems();
    }
  }, [isLoggedIn, isProfileLoading]);

  // REDIRECT IF NOT LOGGED IN
  useEffect(() => {
    if (!isProfileLoading && !isLoggedIn) {
      navigate("/signin", { state: { from: "/cart" } });
    }
  }, [isLoggedIn, navigate, isProfileLoading]);

  // FETCH CART ITEMS FROM BACKEND
  const fetchCartItems = async () => {
    dispatch(setCartLoading(true));
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.getCartProducts || "/api/cart",
      });

      console.log("Backend Cart Response:", response);

      if (response.success && response.data) {
        // Backend returns: { totalItems, totalQuantity, totalAmount, items }
        const { items = [], ...summary } = response.data;
        
        // Update cart items in Redux
        dispatch(setCart(items));
        
        // Update local cart summary
        setCartSummaryData({
          totalItems: summary.totalItems || 0,
          totalQuantity: summary.totalQuantity || 0,
          totalAmount: summary.totalAmount || 0,
          items: items
        });
      } else {
        toast.error("Failed to fetch cart items");
        dispatch(setCart([]));
        setCartSummaryData({ totalItems: 0, totalQuantity: 0, totalAmount: 0, items: [] });
      }
    } catch (err) {
      console.error("Fetch cart error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch cart items");
      dispatch(setCart([]));
      setCartSummaryData({ totalItems: 0, totalQuantity: 0, totalAmount: 0, items: [] });
    } finally {
      dispatch(setCartLoading(false));
    }
  };

  // UPDATE QUANTITY - BOTH INCREASE/DECREASE AND ABSOLUTE
  const handleUpdateQuantity = async (cartItemId, action, absoluteQuantity = null) => {
    setProcessingItems(prev => new Set([...prev, cartItemId]));
    
    try {
      let requestData;
      
      if (absoluteQuantity !== null) {
        // Set absolute quantity
        requestData = { quantity: Math.max(1, absoluteQuantity) };
      } else {
        // Increment/Decrement
        requestData = { quantity: action === "increase" ? 1 : -1 };
      }

      const response = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateQuantity || "/api/cart/item"}/${cartItemId}`,
        data: requestData,
      });

      if (response.success) {
        // Refresh cart to get updated totals from backend
        await fetchCartItems();
        toast.success("Quantity updated successfully");
      } else {
        toast.error("Failed to update quantity");
      }
    } catch (err) {
      console.error("Update quantity error:", err);
      toast.error(err.response?.data?.message || "Failed to update quantity");
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(cartItemId);
        return newSet;
      });
    }
  };

  // REMOVE ITEM FROM CART
  const handleRemoveItem = async (cartItemId) => {
    setProcessingItems(prev => new Set([...prev, cartItemId]));
    
    try {
      const response = await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.removeFromCart || "/api/cart/item"}/${cartItemId}`,
      });

      if (response.success) {
        // Refresh cart to get updated data from backend
        await fetchCartItems();
        toast.success("Item removed from cart");
      } else {
        toast.error("Failed to remove item");
      }
    } catch (err) {
      console.error("Remove item error:", err);
      toast.error(err.response?.data?.message || "Failed to remove item");
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(cartItemId);
        return newSet;
      });
    }
  };

  // CLEAR ENTIRE CART
  const handleClearCart = async () => {
    if (cartItems.length === 0) return;

    if (!window.confirm("Are you sure you want to clear your entire cart?")) {
      return;
    }

    dispatch(setCartLoading(true));
    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.clearCart || "/api/cart/clear",
      });

      if (response.success) {
        dispatch(clearCart());
        setCartSummaryData({ totalItems: 0, totalQuantity: 0, totalAmount: 0, items: [] });
        setAppliedCoupon(null);
        setCouponCode("");
        toast.success("Cart cleared successfully");
      } else {
        toast.error("Failed to clear cart");
      }
    } catch (err) {
      console.error("Clear cart error:", err);
      toast.error(err.response?.data?.message || "Failed to clear cart");
    } finally {
      dispatch(setCartLoading(false));
    }
  };

  // APPLY COUPON
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    setCouponLoading(true);
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.applyCoupon}/${couponCode}`,
      });

      if (response?.success && response?.data) {
        setAppliedCoupon({
          code: couponCode,
          discount: response.data.discountAmount || 0,
          finalTotal: response.data.totalCartValue || cartSummaryData.totalAmount,
        });

        toast.success("Coupon applied successfully");
      } else {
        toast.error(response.message || "Invalid coupon code");
      }
    } catch (err) {
      console.error("Apply coupon error:", err);
      toast.error(err.response?.data?.message || "Failed to apply coupon");
    } finally {
      setCouponLoading(false);
    }
  };

  // REMOVE COUPON
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    // Refresh cart to get original prices
    fetchCartItems();
    toast.success("Coupon removed successfully");
  };

  // HELPER FUNCTIONS MATCHING BACKEND STRUCTURE
  const getProductImage = (item) => {
    // For single items
    if (item.itemType === 'single') {
      if (item.variant?.mainImage) {
        return item.variant.mainImage;
      }
      if (item.variant?.images && item.variant.images.length > 0) {
        return item.variant.images[0];
      }
      if (item.selectedOptions?.color?.image) {
        return item.selectedOptions.color.image;
      }
    }
    
    // For combo items
    if (item.itemType === 'combo' && item.comboDetails?.items?.[0]) {
      // Use first combo item's image as representative
      const firstComboItem = item.comboDetails.items[0];
      if (firstComboItem.image) {
        return firstComboItem.image;
      }
    }
    
    return "/placeholder-product.jpg";
  };

  const getProductName = (item) => {
    if (item.itemType === 'combo') {
      return item.comboDetails?.comboName || item.product?.name || "Combo Product";
    }
    return item.product?.name || "Unknown Product";
  };

  const getProductPrice = (item) => {
    if (item.itemType === 'combo') {
      return item.comboDetails?.comboPrice || 0;
    }
    return item.pricing?.currentPrice || 0;
  };

  const getOriginalPrice = (item) => {
    if (item.itemType === 'combo') {
      return item.comboDetails?.individualTotal || item.comboDetails?.comboPrice || 0;
    }
    return item.pricing?.originalPrice || item.pricing?.currentPrice || 0;
  };

  const getItemTotal = (item) => {
    return item.totals?.itemTotal || 0;
  };

  const getDiscountAmount = (item) => {
    if (item.itemType === 'combo') {
      // For combo, return savings per unit (not total)
      return item.comboDetails?.savings || 0;
    }
    return item.pricing?.discountAmount || 0;
  };

  const getDiscountPercentage = (item) => {
    if (item.itemType === 'combo') {
      const savings = item.comboDetails?.savings || 0;
      const individualTotal = item.comboDetails?.individualTotal || 0;
      return individualTotal > 0 ? Math.round((savings / individualTotal) * 100) : 0;
    }
    return item.pricing?.discountPercentage || 0;
  };

  // CALCULATE FINAL TOTALS WITH COUPON
  const calculateFinalTotals = () => {
    const subtotal = cartSummaryData.totalAmount || 0;
    let discount = 0;
    let total = subtotal;

    // Apply coupon discount
    if (appliedCoupon) {
      discount = appliedCoupon.discount || 0;
      total = appliedCoupon.finalTotal || (total - discount);
    }

    return { 
      subtotal: parseFloat(subtotal.toFixed(2)), 
      discount: parseFloat(discount.toFixed(2)), 
      total: parseFloat(total.toFixed(2))
    };
  };

  const finalTotals = calculateFinalTotals();

  // CHECKOUT
  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    // Check for out of stock items
    const outOfStockItems = cartItems.filter(item => {
      if (item.itemType === 'single') {
        return !item.inventory?.isInStock;
      }
      return false; // Assume combo items are handled in backend validation
    });

    if (outOfStockItems.length > 0) {
      toast.error("Some items in your cart are out of stock");
      return;
    }

    navigate("/checkout", {
      state: {
        cartItems,
        cartSummary: {
          ...cartSummaryData,
          finalTotals
        },
        appliedCoupon: appliedCoupon?.code || null,
      },
    });
  };

  // CONTINUE SHOPPING
  const continueShopping = () => {
    navigate("/shop");
  };

  // LOADING STATE
  if (isProfileLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  // EMPTY CART STATE
  if (cartItems.length === 0 && !cartLoading) {
    return (
      <div className="min-h-[70vh] max-w-6xl mx-auto px-4 py-12 flex flex-col items-center justify-center">
        <div className="text-blue-600 mb-6">
          <ShoppingBag size={64} />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold mb-4">Your Cart is Empty</h1>
        <p className="text-gray-600 mb-8 text-center max-w-md">
          Looks like you haven't added any products to your cart yet.
        </p>
        <button
          onClick={continueShopping}
          className="flex items-center gap-2 px-6 py-3 bg-[#182134] text-white rounded-lg hover:bg-[#2a3441] transition-all"
        >
          <ArrowLeft size={20} />
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-2xl md:text-3xl font-bold mb-8">
        Your Cart ({cartSummaryData.totalQuantity} items)
      </h1>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* CART ITEMS */}
        <div className="lg:w-2/3 w-full">
          {cartLoading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 size={32} className="animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4">
                <div className="flex items-center p-4 border-b bg-blue-50">
                  <ShoppingCart size={20} className="mr-2 text-blue-600" />
                  <div className="text-lg font-semibold text-blue-800">Cart Items</div>
                </div>
                
                {cartItems.map((item) => {
                  // Skip if no product data
                  if (!item.product) {
                    console.warn("Cart item missing product data:", item);
                    return null;
                  }

                  const isProcessing = processingItems.has(item._id);
                  const productName = getProductName(item);
                  const productImage = getProductImage(item);
                  const currentPrice = getProductPrice(item);
                  const originalPrice = getOriginalPrice(item);
                  const itemTotal = getItemTotal(item);
                  const discountAmount = getDiscountAmount(item);
                  const discountPercentage = getDiscountPercentage(item);
                  const priceChanged = item.pricing?.priceChanged || false;

                  return (
                    <div key={item._id} className="p-4 border-b last:border-b-0">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* PRODUCT IMAGE */}
                        <div className="sm:w-24 h-24 flex-shrink-0">
                          <img
                            src={productImage}
                            alt={productName}
                            className="w-full h-full object-contain rounded-md cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => item.product?._id && navigate(`/product/${item.product._id}`)}
                            onError={(e) => {
                              e.target.src = "/placeholder-product.jpg";
                            }}
                          />
                        </div>

                        {/* PRODUCT DETAILS */}
                        <div className="flex-grow">
                          <div className="flex flex-col md:flex-row md:justify-between gap-4">
                            <div className="flex-grow">
                              {/* PRODUCT NAME & STATUS */}
                              <div className="flex items-start gap-2 mb-2">
                                <h3
                                  className="font-medium text-lg cursor-pointer hover:text-blue-600 transition-colors flex-grow"
                                  onClick={() => item.product?._id && navigate(`/product/${item.product._id}`)}
                                >
                                  {productName}
                                </h3>
                                
                                {/* ITEM TYPE BADGE */}
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                  item.itemType === 'combo' 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {item.itemType === 'combo' ? 'COMBO' : 'SINGLE'}
                                </span>

                                {/* LIVE PRICE INDICATOR */}
                                {!priceChanged && item.itemType === 'single' && (
                                  <span className="text-xs text-green-600 font-normal">
                                    Live Price ●
                                  </span>
                                )}
                              </div>

                              {/* VARIANT INFO FOR SINGLE ITEMS */}
                              {item.itemType === 'single' && (
                                <>
                                  {item.variant && (
                                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-1">
                                      {item.variant.color?.name && (
                                        <span>Color: {item.variant.color.name}</span>
                                      )}
                                      {item.variant.size?.name && (
                                        <span>Size: {item.variant.size.name}</span>
                                      )}
                                      {item.variant.sku && (
                                        <span>SKU: {item.variant.sku}</span>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}

                              {/* COMBO DETAILS */}
                              {item.itemType === 'combo' && item.comboDetails && (
                                <div className="mb-3 p-3 bg-purple-50 rounded-lg">
                                  <h4 className="text-sm font-medium text-purple-700 mb-2">Combo Items:</h4>
                                  <div className="space-y-2">
                                    {item.comboDetails.items?.map((comboItem, index) => (
                                      <div key={index} className="flex justify-between items-start">
                                        <div className="flex-grow">
                                          <div className="text-xs text-gray-700 font-medium">
                                            {comboItem.name || `Item ${index + 1}`} (×{comboItem.quantity || 1})
                                          </div>
                                          <div className="text-xs text-gray-500 mt-1">
                                            {comboItem.variantSku && `SKU: ${comboItem.variantSku}`}
                                            {comboItem.size?.name && ` | Size: ${comboItem.size.name}`}
                                            {comboItem.color?.name && ` | Color: ${comboItem.color.name}`}
                                            {!comboItem.size?.name && !comboItem.color?.name && comboItem.variantSku && ' | Standard'}
                                          </div>
                                        </div>
                                        {comboItem.price && (
                                          <span className="text-xs text-gray-500">
                                            ₹{(comboItem.price * comboItem.quantity).toLocaleString()}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Combo Pricing Breakdown */}
                                  <div className="mt-3 pt-2 border-t border-purple-200">
                                    <div className="flex justify-between text-xs mb-1">
                                      <span className="text-gray-600">Individual Total:</span>
                                      <span className="text-gray-600">
                                        ₹{(item.comboDetails.individualTotal || 0).toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex justify-between text-xs mb-1">
                                      <span className="text-purple-700 font-medium">Combo Price:</span>
                                      <span className="text-purple-700 font-medium">
                                        ₹{(item.comboDetails.comboPrice || 0).toLocaleString()}
                                      </span>
                                    </div>
                                    {(item.comboDetails.savings || 0) > 0 && (
                                      <div className="flex justify-between text-xs text-green-600 font-medium">
                                        <span>You Save:</span>
                                        <span>₹{(item.comboDetails.savings || 0).toLocaleString()}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* BRAND & TYPE INFO */}
                              <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-2">
                                {item.product?.brand && (
                                  <span>Brand: {item.product.brand}</span>
                                )}
                                {item.product?.productType && (
                                  <span>Type: {item.product.productType}</span>
                                )}
                              </div>

                              {/* INVENTORY STATUS FOR SINGLE ITEMS */}
                              {item.itemType === 'single' && item.inventory && !item.inventory.isInStock && (
                                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm">
                                  <div className="flex items-center gap-2 text-red-700">
                                    <AlertCircle size={16} />
                                    <span>Out of stock</span>
                                  </div>
                                </div>
                              )}

                              {/* PRICE CHANGE NOTIFICATION */}
                              {priceChanged && (
                                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                                  <div className="flex items-center gap-2 text-blue-700">
                                    <AlertCircle size={16} />
                                    <span>Price updated to current rate!</span>
                                  </div>
                                </div>
                              )}

                              {/* PRICING BREAKDOWN */}
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                  {item.itemType === 'combo' ? 'Combo Pricing:' : 'Item Pricing:'}
                                </h4>
                                
                                {/* Unit Price */}
                                <div className="flex justify-between text-sm mb-1">
                                  <span>
                                    {item.itemType === 'combo' ? 'Combo Price:' : 'Unit Price:'}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">₹{currentPrice.toLocaleString()}</span>
                                    {discountPercentage > 0 && (
                                      <>
                                        <span className="text-gray-500 line-through text-xs">
                                          ₹{originalPrice.toLocaleString()}
                                        </span>
                                        <span className="text-green-600 text-xs font-medium">
                                          {discountPercentage}% off
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Quantity */}
                                <div className="flex justify-between text-sm mb-1">
                                  <span>Quantity:</span>
                                  <span>{item.quantity}</span>
                                </div>

                                {/* Discount Amount */}
                                {discountAmount > 0 && (
                                  <div className="flex justify-between text-sm mb-1 text-green-600">
                                    <span>
                                      {item.itemType === 'combo' ? 'Combo Savings:' : 'Item Discount:'}
                                    </span>
                                    <span>-₹{(discountAmount * item.quantity).toLocaleString()}</span>
                                  </div>
                                )}

                                {/* Item Total */}
                                <div className="flex justify-between text-sm font-bold border-t pt-1 mt-1">
                                  <span>Item Total:</span>
                                  <span className="text-blue-600">₹{itemTotal.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>

                            {/* QUANTITY CONTROLS & REMOVE */}
                            <div className="flex items-start gap-4">
                              {/* QUANTITY CONTROLS */}
                              <div className="flex items-center border rounded-md">
                                <button
                                  onClick={() => handleUpdateQuantity(item._id, "decrease")}
                                  className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-l-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  disabled={isProcessing || (item.quantity || 1) <= 1}
                                >
                                  <ChevronLeft size={16} />
                                </button>
                                <span className="px-4 py-2 border-x min-w-[50px] text-center font-medium">
                                  {item.quantity || 1}
                                </span>
                                <button
                                  onClick={() => handleUpdateQuantity(item._id, "increase")}
                                  className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-r-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  disabled={
                                    isProcessing || 
                                    (item.itemType === 'single' && 
                                     item.inventory && 
                                     item.quantity >= item.inventory.maxQuantityAvailable)
                                  }
                                >
                                  <ChevronRight size={16} />
                                </button>
                              </div>

                              {/* REMOVE BUTTON */}
                              <button
                                onClick={() => handleRemoveItem(item._id)}
                                className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors disabled:opacity-50"
                                disabled={isProcessing}
                                title="Remove item"
                              >
                                {isProcessing ? (
                                  <Loader2 size={18} className="animate-spin" />
                                ) : (
                                  <Trash2 size={18} />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* CART ACTIONS */}
              <div className="flex justify-between items-center">
                <button
                  onClick={continueShopping}
                  className="flex items-center gap-2 text-blue-600 hover:underline transition-colors"
                >
                  <ChevronLeft size={16} />
                  Continue Shopping
                </button>

                <button
                  onClick={handleClearCart}
                  className="flex items-center gap-2 text-red-500 hover:text-red-700 transition-colors"
                  disabled={cartLoading}
                >
                  <Trash2 size={16} />
                  Clear Cart
                </button>
              </div>
            </>
          )}
        </div>

        {/* ORDER SUMMARY */}
        <div className="lg:w-1/3 w-full">
          <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
            <h2 className="text-xl font-bold mb-6">Order Summary</h2>

            {/* SUMMARY BREAKDOWN */}
            <div className="space-y-3 mb-6">
              <div className="flex justify-between pb-3 border-b">
                <span className="text-gray-600">
                  Subtotal ({cartSummaryData.totalQuantity} items)
                </span>
                <span className="font-medium">₹{finalTotals.subtotal.toLocaleString()}</span>
              </div>

              {/* COUPON DISCOUNT */}
              {appliedCoupon && finalTotals.discount > 0 && (
                <div className="flex justify-between items-center pb-3 border-b text-green-600">
                  <div className="flex items-center gap-2">
                    <Tag size={16} />
                    <span>Coupon: {appliedCoupon.code}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium">-₹{finalTotals.discount.toLocaleString()}</span>
                    <button
                      onClick={removeCoupon}
                      className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                      title="Remove coupon"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-between font-bold text-lg pt-3 bg-blue-50 px-3 py-2 rounded-lg">
                <span>Total Amount</span>
                <span className="text-blue-600">₹{finalTotals.total.toLocaleString()}</span>
              </div>
            </div>

            {/* SAVINGS SUMMARY */}
            {(() => {
              // Calculate product discounts correctly
              const totalProductDiscounts = cartItems.reduce((total, item) => {
                const discountAmount = getDiscountAmount(item);
                // For combo items, savings is already calculated per combo, so just multiply by quantity
                // For single items, discount is per unit, so multiply by quantity
                return total + (discountAmount * item.quantity);
              }, 0);
              
              const totalSavings = totalProductDiscounts + finalTotals.discount;
              
              if (totalSavings > 0) {
                return (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-green-700 text-sm font-medium">
                      🎉 You're saving ₹{totalSavings.toLocaleString()} on this order!
                    </div>
                    {totalProductDiscounts > 0 && (
                      <div className="text-green-600 text-xs mt-1">
                        Product discounts: ₹{totalProductDiscounts.toLocaleString()}
                      </div>
                    )}
                    {finalTotals.discount > 0 && (
                      <div className="text-green-600 text-xs mt-1">
                        Coupon discount: ₹{finalTotals.discount.toLocaleString()}
                      </div>
                    )}
                  </div>
                );
              }
            })()}

            {/* COUPON SECTION */}
            {!appliedCoupon && !userProfile?.isAlreadyCouponUsed && (
              <div className="mb-6">
                <div className="flex gap-0">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="flex-grow p-3 border border-r-0 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={couponLoading}
                  />
                  <button
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    className="bg-blue-600 text-white px-6 py-3 rounded-r-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[80px] hover:bg-blue-700 transition-colors"
                  >
                    {couponLoading ? <Loader2 size={16} className="animate-spin" /> : "Apply"}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Have a coupon? Enter the code above to get discount.
                </p>
              </div>
            )}

            {/* CHECKOUT BUTTON */}
            <button
              onClick={handleCheckout}
              disabled={
                cartItems.length === 0 || 
                cartLoading || 
                cartItems.some(item => 
                  item.itemType === 'single' && 
                  item.inventory && 
                  !item.inventory.isInStock
                )
              }
              className="w-full bg-[#182134] text-white py-4 rounded-lg font-medium hover:bg-[#2a3441] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {cartLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 size={20} className="animate-spin" />
                  Processing...
                </div>
              ) : cartItems.some(item => 
                  item.itemType === 'single' && 
                  item.inventory && 
                  !item.inventory.isInStock
                ) ? (
                "Some items are out of stock"
              ) : (
                `Proceed to Checkout (₹${finalTotals.total.toLocaleString()})`
              )}
            </button>

            {/* SECURITY INFO */}
            <div className="text-xs text-gray-500 mt-4 text-center">
              <p>🔒 Secure checkout with 256-bit SSL encryption</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;